import type { Tone } from "./orders";
import type { Inquiry, InboxDocument } from "./types";

// What the Inquiries and Documents screens work out: an inquiry's state in words and whether someone must reply to it,
// and a document's state. Pure, so it is tested without a browser.

const INQUIRY: Record<string, { text: string; tone: Tone; needsReply: boolean }> = {
  NOTIFIED: { text: "Sent to sales@", tone: "good", needsReply: false },
  NEW: { text: "Just arrived", tone: "neutral", needsReply: false },
  HELD: { text: "Held: read it and reply from sales@", tone: "late", needsReply: true },
  NOTICE_FAILED: { text: "Not sent to sales@: read it and reply", tone: "late", needsReply: true },
  SPAM: { text: "Set aside as a bot", tone: "neutral", needsReply: false },
};

export function inquiryState(status: string): { text: string; tone: Tone; needsReply: boolean } {
  return INQUIRY[status] ?? { text: status.toLowerCase().replace(/_/g, " "), tone: "neutral", needsReply: false };
}

/** Those needing a reply first, then newest first. */
export function inquiryOrder(rows: Inquiry[]): Inquiry[] {
  return [...rows].sort((a, b) => Number(inquiryState(b.status).needsReply) - Number(inquiryState(a.status).needsReply) ||
    b.receivedAt.localeCompare(a.receivedAt));
}

const DOCUMENT: Record<string, { text: string; tone: Tone }> = {
  NEEDS_REVIEW: { text: "Needs a look", tone: "soon" },
  FAILED: { text: "Couldn't be read", tone: "late" },
  EXTRACTED: { text: "Read", tone: "good" },
  IGNORED: { text: "Not read", tone: "neutral" },
  RECEIVED: { text: "Waiting to be read", tone: "neutral" },
  EXTRACTING: { text: "Being read", tone: "neutral" },
};

export function documentState(d: Pick<InboxDocument, "status">): { text: string; tone: Tone } {
  return DOCUMENT[d.status] ?? { text: d.status.toLowerCase().replace(/_/g, " "), tone: "neutral" };
}

/** A documents pile in the address: the senders Tare knows (default), the ones it doesn't, or all. */
export type Pile = "known" | "unknown" | "all";

export function pileOf(value: string | null): Pile {
  return value === "unknown" || value === "all" ? value : "known";
}

export function senderArg(pile: Pile): "KNOWN" | "UNKNOWN" | "ALL" {
  return pile === "known" ? "KNOWN" : pile === "unknown" ? "UNKNOWN" : "ALL";
}
